/**
 * YAML → JSON Tool
 *
 * Converts YAML into JSON.
 */

import { parse as parseYaml } from 'yaml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

type IndentSize = '0' | '2' | '4' | 'tab';

export const YamlToJsonTool: ITransformerTool = {
  id: 'yaml-to-json',
  name: 'YAML → JSON',
  description: 'Convert YAML to JSON',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['yaml', 'json', 'convert', 'transform'],

  supportedInputTypes: [SEMANTIC_TYPES.YAML],
  outputType: SEMANTIC_TYPES.JSON,

  attributes: [
    {
      name: 'indentSize',
      type: 'select',
      label: 'Indent Size',
      defaultValue: '2',
      options: ['0', '2', '4', 'tab'],
      description: 'JSON indentation (0=minified)',
    },
    {
      name: 'sortKeys',
      type: 'boolean',
      label: 'Sort Keys',
      defaultValue: false,
      description: 'Sort object keys alphabetically',
    },
  ],

  supportsInput(input: string, detectedType?: string): boolean {
    if (detectedType !== 'YAML') return false;
    try {
      parseYaml(input);
      return true;
    } catch {
      return false;
    }
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    let parsed: unknown;

    try {
      parsed = parseYaml(input);
    } catch (e) {
      const error = e as Error;
      throw ToolError.validation(`Invalid YAML: ${error.message}`, {
        suggestion: 'Check indentation and ensure mappings use "key: value" syntax',
      });
    }

    if (config.sortKeys) {
      parsed = sortObjectKeys(parsed);
    }

    const indentSize = (config.indentSize as IndentSize) || '2';
    const indent = indentSize === 'tab' ? '\t' : parseInt(indentSize, 10);

    return JSON.stringify(parsed, null, indent);
  },
};

function sortObjectKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }

  if (obj !== null && typeof obj === 'object') {
    const sorted: Record<string, unknown> = {};
    const keys = Object.keys(obj as Record<string, unknown>).sort();
    for (const key of keys) {
      sorted[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
    }
    return sorted;
  }

  return obj;
}
